import { useCallback, useContext, useMemo } from 'react';
import { MoneyCurrencyContext } from '@/components/currency/money-currency-context';
import { useTranslation } from 'react-i18next';
import { useClinicTimezone } from '@/hooks/useClinicTimezone';
import { useAuth } from '@/contexts/AuthContext';
import { useBranch } from '@/contexts/BranchContext';
import { DEFAULT_CURRENCY } from '@/utils/money';
import {
  durationParts,
  formatMoneyIntl,
  formatNumberIntl,
  minutesBetween,
  type FormatMoneyOptions,
} from '@/utils/format-locale';

/**
 * The formatting surface a screen should use — money, numbers, dates and
 * durations, all already bound to the active language and the active clinic's
 * timezone.
 *
 * Two things it removes from call sites:
 *
 *  1. **`toLocaleString()` / `.toFixed(2)` scattered in components.** Those
 *     read the *browser's* locale and zone, so a Cairo clinic viewed from a
 *     laptop still on European time showed the wrong hour on a clinical
 *     record, and Arabic showed Latin digits.
 *  2. **Threading `locale` and `timezone` through props.** Both come from
 *     context; a component that formats should just ask.
 *
 * Dates delegate to `useClinicTimezone().format`, which is already the app's
 * correct answer for "what time did this happen" — it renders the clinic's
 * wall clock, not the viewer's.
 */
export function useFormatters() {
  const { i18n, t } = useTranslation();
  const { format: formatInZone, timezone } = useClinicTimezone();

  const locale = i18n.language || 'en';
  const { clinic } = useAuth();
  const { selectedBranch, isAllBranches, branches } = useBranch();

  /** The clinic's base currency — what consolidated figures are in. */
  const baseCurrency = clinic?.currencyCode || DEFAULT_CURRENCY;
  /**
   * The SCOPE currency: the selected branch's, or the base on "All branches".
   * An amount with no currency of its own is in this. A record that carries a
   * `currencyCode` (an invoice, an account) must pass it — see `<Money>`.
   */
  const recordCurrency = useContext(MoneyCurrencyContext);
  const currency =
    recordCurrency ||
    (!isAllBranches && selectedBranch?.currencyCode
      ? selectedBranch.currencyCode
      : baseCurrency);
  /** True once any branch works in a currency other than the base. */
  const isMultiCurrency = branches.some(
    (b) => (b.currencyCode || baseCurrency) !== baseCurrency,
  );

  const money = useCallback(
    (value: number | string | null | undefined, opts?: FormatMoneyOptions) =>
      formatMoneyIntl(value, { locale, currency, ...opts }),
    [locale, currency],
  );

  const number = useCallback(
    (
      value: number | string | null | undefined,
      opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
    ) => formatNumberIntl(value, { locale, ...opts }),
    [locale],
  );

  /**
   * Dates and times through `Intl`, in the clinic's zone and the active
   * language. They used date-fns with no locale, so Arabic screens showed
   * English month names and Latin digits beside Arabic money. English output
   * is unchanged: en-GB day-month order for dates, 12-hour times.
   */
  const dateFmt = useMemo(() => {
    const isEn = locale.startsWith('en');
    const safe = (fn: () => Intl.DateTimeFormat) => {
      try {
        return fn();
      } catch {
        return null;
      }
    };
    return {
      date: safe(
        () =>
          new Intl.DateTimeFormat(isEn ? 'en-GB' : locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: timezone,
          }),
      ),
      time: safe(
        () =>
          new Intl.DateTimeFormat(isEn ? 'en-US' : locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone,
          }),
      ),
    };
  }, [locale, timezone]);

  const toDate = (instant: string | Date) => {
    const d = typeof instant === 'string' ? new Date(instant) : instant;
    return isNaN(d.getTime()) ? null : d;
  };

  /** "22 Aug 2026" — a date with no time. */
  const date = useCallback(
    (instant: string | Date | null | undefined) => {
      if (!instant) return '';
      const d = toDate(instant);
      if (!d) return '';
      return dateFmt.date
        ? dateFmt.date.format(d)
        : formatInZone(d, 'd MMM yyyy');
    },
    [dateFmt, formatInZone],
  );

  /** "9:45 AM" — clinic wall clock. */
  const time = useCallback(
    (instant: string | Date | null | undefined) => {
      if (!instant) return '';
      const d = toDate(instant);
      if (!d) return '';
      return dateFmt.time ? dateFmt.time.format(d) : formatInZone(d, 'h:mm a');
    },
    [dateFmt, formatInZone],
  );

  /** "22 Aug 2026, 9:45 AM". */
  const dateTime = useCallback(
    (instant: string | Date | null | undefined) => {
      if (!instant) return '';
      const d = date(instant);
      return d ? `${d}, ${time(instant)}` : '';
    },
    [date, time],
  );

  /**
   * A span of minutes as a translated phrase: "59 min", "1 h 5 min", "2 h".
   * Returns '' for null/undefined so callers can render it directly.
   */
  const duration = useCallback(
    (totalMinutes: number | null | undefined) => {
      const parts = durationParts(totalMinutes);
      if (!parts) return '';
      const { hours, minutes } = parts;
      if (hours > 0 && minutes > 0)
        return t('common.duration.hoursMinutes', { hours, minutes });
      if (hours > 0) return t('common.duration.hours', { hours });
      return t('common.duration.minutes', { minutes });
    },
    [t],
  );

  /**
   * "2 days ago", "in 3 hours" — localised, in all four languages.
   *
   * Pairs with `date`/`dateTime` rather than replacing them: an exact timestamp
   * answers "when precisely", a relative one answers "is this stale", and a
   * conversation thread needs both. `Intl.RelativeTimeFormat` handles the
   * plural and gender agreement that a hand-rolled version gets wrong in
   * Arabic, French and German.
   */
  const relative = useCallback(
    (instant: string | Date | null | undefined) => {
      if (!instant) return '';
      const then = new Date(instant).getTime();
      if (Number.isNaN(then)) return '';

      const seconds = Math.round((then - Date.now()) / 1000);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

      // Largest unit that still yields a value of at least one, so a
      // three-week-old ticket reads "3 weeks ago" rather than "21 days ago".
      const units: [Intl.RelativeTimeFormatUnit, number][] = [
        ['year', 60 * 60 * 24 * 365],
        ['month', 60 * 60 * 24 * 30],
        ['week', 60 * 60 * 24 * 7],
        ['day', 60 * 60 * 24],
        ['hour', 60 * 60],
        ['minute', 60],
      ];

      for (const [unit, secondsInUnit] of units) {
        if (Math.abs(seconds) >= secondsInUnit) {
          return rtf.format(Math.round(seconds / secondsInUnit), unit);
        }
      }
      return rtf.format(0, 'second');
    },
    [locale],
  );

  /**
   * A percentage in 0–100 — "12.5%" / "١٢٫٥٪". THE rule for every ratio on a
   * screen: one decimal place, locale digits and sign, bidi-isolated so the
   * sign cannot migrate across the number inside Arabic text. Reports used
   * to mix `0%`, `0.0%` and `0.00%` on the same page.
   *
   * `null` renders as an em dash: a ratio over nothing has no value, and 0%
   * would be a claim.
   */
  const percent = useCallback(
    (
      value: number | null | undefined,
      opts?: { signed?: boolean; fractionDigits?: number },
    ) => {
      if (value === null || value === undefined || !Number.isFinite(value))
        return '—';
      const digits = opts?.fractionDigits ?? 1;
      const body = new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        signDisplay: opts?.signed ? 'exceptZero' : 'auto',
      }).format(value / 100);
      return `⁨${body}⁩`;
    },
    [locale],
  );

  /** A whole count with locale digits and grouping. */
  const count = useCallback(
    (value: number | null | undefined) =>
      formatNumberIntl(value ?? 0, {
        locale,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  return useMemo(
    () => ({
      locale,
      currency,
      baseCurrency,
      isMultiCurrency,
      timezone,
      money,
      number,
      percent,
      count,
      date,
      time,
      dateTime,
      relative,
      duration,
      minutesBetween,
    }),
    [
      locale,
      currency,
      baseCurrency,
      isMultiCurrency,
      timezone,
      money,
      number,
      percent,
      count,
      date,
      time,
      dateTime,
      relative,
      duration,
    ],
  );
}
